import React, { useCallback, useContext, useState, useEffect } from 'react';
import {
  Route,
  useHistory,
  useLocation,
  useParams
} from 'react-router-dom';
import api from 'Api';
import useConfigSettings from 'Hooks/useConfigSettings';
import useHandleError from 'Utils/handleError';
import LoadingSpinner from 'Components/shared/LoadingSpinner';
import { AuthContext } from 'States/auth/authState';
import { ConfigContext } from 'States/config/configState';

import RedemptionDetailsForm from './RedemptionDetailsForm';
import { KycContext } from 'States/kyc/kycState';
import { ROUTES } from 'Router/Routes';

const RedeemDetails = () => {
  const history = useHistory();
  const location = useLocation();
  const {
    id: currentMethodId
  } = useParams();
  const {
    setConfig: setCurrentlyLoadedConfig,
    config
  } = useContext(ConfigContext);
  const { isKycEnabled, currentKycTier } = useContext(KycContext);
  const { isAdmin } = useContext(AuthContext);
  const {
    data: RestrictedMode
  } = useConfigSettings.query({
    select: useCallback(({ RestrictedMode }) => RestrictedMode, [])
  });

  const [redemptionMethod, setRedemptionMethod] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const handleError = useHandleError();

  useEffect(() => {
    const getRedemptionMethodFromConfig = async () => {
      try {
        const configBE = await api.Config.getFullConfig();
        setCurrentlyLoadedConfig(configBE);
        const methodConfig = location.state?.redemptionConfig
          || await api.Redemptions.getMethodConfig(currentMethodId);
        if (!methodConfig) {
          history.replace(ROUTES.custom.investments);
        } else {
          const {
            UnitOfMeasureCode,
            CurrencyCode,
            DecimalPrecision,
            Translations
          } = location.state?.asset
            || await api.Assets.getAsset(methodConfig.UserPays.UniqueAssetId);
          const redemptionMethod = {
            ...methodConfig,
            UserPays: {
              ...methodConfig.UserPays,
              UnitOfMeasureCode,
              CurrencyCode,
              DecimalPrecision,
              Translations
            }
          }
          setRedemptionMethod(redemptionMethod);
        }
      } catch (error) {
        handleError({ error })
      }
    };
    getRedemptionMethodFromConfig();
  }, []);

  useEffect(() => {
    const checkKycTier = () => {
      const { kycTierFunctionalRestrictions } = config;
      const redemptionMethodTier = redemptionMethod?.KycTier || 0;
      const redemptionAvailability =
        kycTierFunctionalRestrictions?.availability?.redemption || 0;
      const redemptionVisibility =
        kycTierFunctionalRestrictions?.visibility?.redemption || 0;
      const requiredTier = Math.max(
        redemptionMethodTier,
        Math.max(redemptionAvailability, redemptionVisibility)
      );

      const isUserTierSufficient =
        !isKycEnabled || isAdmin || currentKycTier >= requiredTier;

      if (!isUserTierSufficient) {
        history.replace(ROUTES.custom.investments);
      }
      setIsLoading(false);
    }
    if (redemptionMethod) {
      RestrictedMode ? setIsLoading(false) : checkKycTier();
    }
  }, [redemptionMethod, RestrictedMode]);

  return (
    <>
      {isLoading ? (
        <LoadingSpinner
          classes="mr-auto ml-auto"
          dataQa="loading-page"
        />
      ) : (
        <div className="flex flex-col gap-16 sm:gap-24">
          <Route path={ROUTES.redemptionForm}>
            <RedemptionDetailsForm
              redemptionMethod={redemptionMethod}
            />
          </Route>
        </div>
      )}
    </>
  );
};

export default RedeemDetails;
